import { useMemo } from "react";
import { format } from "date-fns";
import { Button, Icon, Loading, Modal, Spinner, Tooltip, Typography } from "@/components/atoms";
import { getErrorData, getErrorMessages } from "@/helpers/reduxHelpers";
import { useProfile } from "@/hooks";
import { useGetVehicleFormOptionsQuery, useVerifyVehicleMutation } from "@/redux/apis/vehicle/vehicleApi";
import { RawErrorResponse } from "@/redux/types";
import { Vehicle } from "@/redux/slices/vehicle/types";
import { addToast, formatCurrency, getCountryFlagSrc } from "@/utils";
import { RegistrationRenewalNotice } from "../../common/RegistrationRenewalNotice";
import { VehicleVerificationErrorAlert } from "../../AddVehicle/common/VehicleVerificationErrorAlert";
import { getVerificationErrorType } from "../../AddVehicle/helpers";

interface UpdateVerificationModalProps extends React.HTMLAttributes<HTMLDivElement> {
  vehicle: Vehicle;
  open: boolean;
  onClose: () => void;
}

export const UpdateVerificationModal = ({ vehicle, open, onClose, ...props }: UpdateVerificationModalProps) => {
  const [verifyVehicle, { isLoading, error }] = useVerifyVehicleMutation();
  const { data } = useGetVehicleFormOptionsQuery();
  const { dateFormat } = useProfile();
  const fee = useMemo(() => {
    if (!data) return 0;
    return data.fees.verificationFee;
  }, [data]);

  const errorType = useMemo(() => {
    if (!error) return undefined;
    return getVerificationErrorType(error);
  }, [error]);

  const handleVerify = () => {
    verifyVehicle(vehicle.uuid)
      .unwrap()
      .then(() => {
        addToast("success", "Vehicle registration reconfirmed successfully");
        onClose();
      })
      .catch((e) => {
        const errorData = getErrorData(e as RawErrorResponse);
        if (errorData?.error === "VIN_MISMATCH") {
          addToast("danger", getErrorMessages(e as RawErrorResponse)[0] || "VIN mismatch detected");
        } else {
          addToast("danger", getErrorMessages(e as RawErrorResponse)[0] || "Failed to verify vehicle");
        }
        onClose();
      });
  };

  const flagSrc = getCountryFlagSrc(vehicle.country.countryCode);

  return (
    <>
      <Modal open={open} onClose={onClose} className="!max-w-[550px] !p-6">
        <div className="relative" {...props}>
          {isLoading && <Loading />}

          <Typography variant="title">Confirm Updated Vehicle Details</Typography>
          <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
            Review the details below to ensure everything matches your vehicle&apos;s latest registration and information before proceeding
          </Typography>
          <RegistrationRenewalNotice />

          {errorType && <VehicleVerificationErrorAlert errorType={errorType} />}

          <div className="mt-4 grid grid-cols-2 gap-3">
            <DetailRow label="Make" value={vehicle.model.make} />
            <DetailRow label="Model" value={vehicle.model.model} />
            <DetailRow label="Plate Number" value={vehicle.registrationNumber} />
            <DetailRow label="Year" value={vehicle.year?.toString() ?? "-"} />
            <DetailRow
              label="Color"
              value={
                vehicle.color ? (
                  <span className="inline-flex items-center gap-2 capitalize">
                    <span
                      className="inline-block h-3 w-3 rounded-full border border-black"
                      style={{ backgroundColor: vehicle.color.toLowerCase().replace("_", "") }}
                    />
                    {vehicle.color.toLowerCase().replace("_", " ")}
                  </span>
                ) : (
                  "-"
                )
              }
            />
            <DetailRow label="Engine Type" value={vehicle.model.engine ? vehicle.model.engine.toLowerCase() : "-"} />
            <DetailRow label="Passenger Capacity" value={`${vehicle.capacity.pax} pax`} />
            <DetailRow
              label="State Registered"
              value={
                <span className="inline-flex items-center gap-1">
                  {flagSrc && <img src={flagSrc} className="h-[18px] w-[18px] rounded-full object-cover" alt="" />}
                  {vehicle.state.abbreviation}
                </span>
              }
            />
            <DetailRow
              label="Registration Expiry"
              value={vehicle.registration.expiryDate ? format(new Date(vehicle.registration.expiryDate), dateFormat) : "-"}
            />
            <DetailRow label="VIN" value={vehicle.vin ?? "-"} />
            <DetailRow
              label={
                <span className="inline-flex items-center gap-1">
                  Vehicle Category
                  <Tooltip content="System-defined category based on vehicle make/model" placement="top">
                    <Icon name="InfoCircle" variant="Bold" className="text-neutral-dark-gray" size="sm" />
                  </Tooltip>
                </span>
              }
              value={vehicle.vehicleCategory?.name ?? "-"}
            />
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <Button variant="secondary" size="md" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" size="md" onClick={handleVerify} disabled={isLoading}>
              {isLoading ? <Spinner /> : `Reconfirm Registration for ${formatCurrency(fee, 2, "AUD")}`}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

const DetailRow = ({ label, value }: { label: React.ReactNode; value: React.ReactNode }) => (
  <>
    <div className="col-span-1 flex items-center">
      <Typography className="text-neutral-dark-gray">{label}</Typography>
    </div>
    <div className="col-span-1 flex items-center justify-end">
      <Typography className="capitalize text-neutral-black">{value}</Typography>
    </div>
  </>
);
